import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Users,
  Settings,
  Building2,
  ClipboardList,
  DollarSign,
  MessageSquare,
  FileText,
  LogOut,
  Bell,
  X,
  UserCircle,
} from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationService } from '../services/notificationService';
import LanguageSwitcher from './LanguageSwitcher';

const iconProps = { className: 'w-5 h-5 flex-shrink-0', strokeWidth: 1.75 };

const Sidebar = () => {
  const { userData, logout, currentUser } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const [openPanel, setOpenPanel] = useState(false);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getNavItems = () => {
    if (!userData) return [];

    switch (userData.role) {
      case 'admin':
        return [
          { name: 'Dashboard', path: '/admin', Icon: LayoutDashboard },
          { name: 'Reports', path: '/admin/reports', Icon: BarChart3 },
          { name: 'Users', path: '/admin/users', Icon: Users },
          { name: 'System', path: '/admin/system', Icon: Settings },
        ];
      case 'owner':
        return [
          { name: t('sidebar.dashboard'), path: '/dashboard', Icon: LayoutDashboard },
          { name: t('sidebar.properties'), path: '/properties', Icon: Building2 },
          { name: t('sidebar.tenants'), path: '/tenants', Icon: Users },
          { name: t('sidebar.tickets'), path: '/tickets', Icon: ClipboardList },
          { name: t('sidebar.expenses'), path: '/expenses', Icon: DollarSign },
          { name: t('sidebar.chats'), path: '/chats', Icon: MessageSquare },
          { name: 'Profile', path: '/profile', Icon: UserCircle },
        ];
      case 'tenant':
        return [
          { name: 'Dashboard', path: '/dashboard', Icon: LayoutDashboard },
          { name: 'Maintenance Requests', path: '/tickets', Icon: ClipboardList },
          { name: 'My Property', path: '/property', Icon: Building2 },
          { name: 'Documents', path: '/documents', Icon: FileText },
          { name: 'Profile', path: '/profile', Icon: UserCircle },
          { name: 'Settings', path: '/settings', Icon: Settings },
          { name: 'Chats', path: '/chats', Icon: MessageSquare },
        ];
      case 'serviceProvider':
        return [
          { name: 'Dashboard', path: '/dashboard', Icon: LayoutDashboard },
          { name: 'My Jobs', path: '/tickets', Icon: ClipboardList },
          { name: t('sidebar.earnings'), path: '/earnings', Icon: DollarSign },
          { name: 'Chats', path: '/chats', Icon: MessageSquare },
          { name: 'Profile', path: '/profile', Icon: UserCircle },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  useEffect(() => {
    if (!currentUser) return;
    const unsubCount = notificationService.subscribeToUnreadCount(currentUser.uid, setUnreadCount);
    const unsubRecent = notificationService.subscribeToRecent(currentUser.uid, setRecent);
    return () => {
      unsubCount && unsubCount();
      unsubRecent && unsubRecent();
    };
  }, [currentUser?.uid]);

  return (
    <div className="w-72 bg-white fixed h-screen border-r border-slate-200 flex flex-col shadow-sm">
      <div className="p-5 flex-1">
        <div className="flex items-center gap-3 mb-6">
          <Logo type="icon" width="48px" height="48px" className="flex-shrink-0" />
          <div>
            <h1 className="text-base font-semibold text-slate-800">EasyProperty</h1>
            <p className="text-xs text-slate-500">Management Platform</p>
          </div>
        </div>

        {/* Language Switcher */}
        <div className="mb-6">
          <LanguageSwitcher />
        </div>

        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.Icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                  isActive(item.path)
                    ? userData?.role === 'admin'
                      ? 'bg-emerald-50 text-emerald-800 font-medium border border-emerald-200'
                      : 'bg-slate-100 text-slate-800 font-medium border border-slate-200'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                <Icon {...iconProps} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="p-5 border-t border-slate-200 relative">
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm uppercase">
                {userData?.displayName?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800 truncate">
                {userData?.displayName || 'User'}
              </p>
              <p className="text-xs text-slate-500 capitalize">{userData?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            <span>{t('sidebar.logout')}</span>
          </button>
          

          <div className="mt-4">
            <button
              onClick={() => setOpenPanel((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-700"
            >
              <span className="flex items-center gap-2">
                <Bell className="w-4 h-4" strokeWidth={1.75} />
                <span>{t('sidebar.notifications')}</span>
              </span>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs bg-red-500 text-white rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {openPanel && (
          <div className="absolute left-4 right-4 bottom-[5.5rem] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-medium text-slate-800">{t('sidebar.notifications')}</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => notificationService.markAllAsRead(currentUser.uid)}
                  className="text-xs text-slate-600 hover:text-slate-800 font-medium"
                >
                  {t('sidebar.markAllRead')}
                </button>
                <button onClick={() => setOpenPanel(false)} className="p-1 text-slate-500 hover:text-slate-700 rounded">
                  <X className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {recent.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-500">{t('sidebar.noNotifications')}</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {recent.map((n) => (
                    <li key={n.id} className="px-4 py-3 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="pr-3">
                          <p className="text-sm text-slate-800">
                            {n.title || (n.type === 'chat_message' ? 'New message' : 'Notification')}
                          </p>
                          <p className="text-xs text-slate-600 mt-0.5">
                            {n.body || (n.type === 'chat_message' ? 'Open chat to view the message.' : '')}
                          </p>
                          {n.createdAt && (
                            <p className="text-[11px] text-slate-400 mt-1">
                              {n.createdAt.toDate?.().toLocaleString?.() || ''}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          {!n.read && (
                            <span className="mt-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-slate-500" />
                          )}
                          {n.resourceType === 'conversation' && n.resourceId && (
                            <button
                              onClick={async () => {
                                try {
                                  // Optimistically update UI
                                  setRecent((prev) => prev.filter((r) => r.id !== n.id));
                                  if (!n.read) setUnreadCount((c) => Math.max(0, c - 1));
                                  await notificationService.deleteNotification(n.id);
                                  setOpenPanel(false);
                                  navigate(`/chats?conversation=${n.resourceId}`);
                                } catch (e) {
                                  console.error('Failed to open notification:', e);
                                  // Navigate anyway if deletion fails
                                  setOpenPanel(false);
                                  navigate(`/chats?conversation=${n.resourceId}`);
                                }
                              }}
                              className="text-xs text-slate-600 hover:text-slate-800 font-medium"
                            >
                              Open
                            </button>
                          )}
                          {n.resourceType === 'ticket' && n.resourceId && (
                            <button
                              onClick={async () => {
                                try {
                                  setRecent((prev) => prev.filter((r) => r.id !== n.id));
                                  if (!n.read) setUnreadCount((c) => Math.max(0, c - 1));
                                  await notificationService.deleteNotification(n.id);
                                  setOpenPanel(false);
                                  navigate(`/tickets/${n.resourceId}`);
                                } catch (e) {
                                  console.error('Failed to open ticket notification:', e);
                                  setOpenPanel(false);
                                  navigate(`/tickets/${n.resourceId}`);
                                }
                              }}
                              className="text-xs text-slate-600 hover:text-slate-800 font-medium"
                            >
                              Open
                            </button>
                          )}
                          <button
                            onClick={() => notificationService.deleteNotification(n.id)}
                            className="text-xs text-slate-400 hover:text-slate-600"
                            title="Dismiss"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar; 